import { readCookieConsent } from "./cookieConsent";

const GA_SCRIPT_ID = "anylex-ga4-script";
const YM_SCRIPT_ID = "anylex-yandex-metrika-script";
const DATA_LAYER_NAME = "dataLayer";
const CONFIG_URL = `${(process.env.REACT_APP_API_BASE_URL || "").trim()}/api/v1/public/marketing/analytics-config`;

let analyticsInitialized = false;
// ID счётчиков задаются в админке (Маркетинг → Интеграции); env остаётся запасным вариантом
let remoteConfig = {
  ga4_measurement_id: (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim(),
  yandex_metrika_id: (process.env.REACT_APP_YANDEX_METRIKA_ID || "").trim(),
  yandex_metrika_webvisor: false,
};
let configPromise = null;

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function getGaMeasurementId() {
  return (remoteConfig.ga4_measurement_id || "").trim();
}

function getYandexMetrikaId() {
  return (remoteConfig.yandex_metrika_id || "").trim();
}

function loadRemoteConfig() {
  if (configPromise) {
    return configPromise;
  }
  configPromise = fetch(CONFIG_URL, { credentials: "omit" })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (data) {
        remoteConfig = {
          ga4_measurement_id: data.ga4_measurement_id || remoteConfig.ga4_measurement_id,
          yandex_metrika_id: data.yandex_metrika_id || remoteConfig.yandex_metrika_id,
          yandex_metrika_webvisor: Boolean(data.yandex_metrika_webvisor),
        };
      }
      return remoteConfig;
    })
    .catch(() => remoteConfig);
  return configPromise;
}

function canUseAnalytics() {
  return Boolean(readCookieConsent()?.analytics);
}

function ensureGaScript(measurementId) {
  if (!measurementId || document.getElementById(GA_SCRIPT_ID)) {
    return;
  }

  window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window[DATA_LAYER_NAME].push(arguments);
    };

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });
}

function ensureYandexMetrikaScript(counterId) {
  if (!counterId || document.getElementById(YM_SCRIPT_ID)) {
    return;
  }

  (function initMetrika(m, e, t, r, i, k, a) {
    m[i] =
      m[i] ||
      function ymProxy() {
        (m[i].a = m[i].a || []).push(arguments);
      };
    m[i].l = 1 * new Date();
    k = e.createElement(t);
    a = e.getElementsByTagName(t)[0];
    k.async = 1;
    k.src = r;
    k.id = YM_SCRIPT_ID;
    a.parentNode.insertBefore(k, a);
  })(
    window,
    document,
    "script",
    "https://mc.yandex.ru/metrika/tag.js",
    "ym"
  );

  window.ym(Number(counterId), "init", {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: Boolean(remoteConfig.yandex_metrika_webvisor),
  });
}

export function initAnalyticsIfAllowed(consent = readCookieConsent()) {
  if (!isBrowser() || analyticsInitialized || !consent?.analytics) {
    return;
  }

  analyticsInitialized = true;
  loadRemoteConfig().then(() => {
    ensureGaScript(getGaMeasurementId());
    ensureYandexMetrikaScript(getYandexMetrikaId());
  });
}

function sanitizeGoalName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

export function trackEvent(eventName, params = {}) {
  if (!isBrowser() || !canUseAnalytics()) {
    return;
  }

  initAnalyticsIfAllowed();

  // ID счётчиков приходят с бэкенда — ждём конфиг, иначе первое событие потеряется
  loadRemoteConfig().then(() => {
    const gaMeasurementId = getGaMeasurementId();
    const yandexMetrikaId = getYandexMetrikaId();

    if (gaMeasurementId && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }

    if (yandexMetrikaId && typeof window.ym === "function") {
      window.ym(Number(yandexMetrikaId), "reachGoal", sanitizeGoalName(eventName), params);
    }
  });
}

export function trackPageView(locationLike) {
  if (!isBrowser() || !canUseAnalytics()) {
    return;
  }

  initAnalyticsIfAllowed();

  const path = `${locationLike?.pathname || window.location.pathname}${
    locationLike?.search || window.location.search || ""
  }`;
  const title = document.title;

  loadRemoteConfig().then(() => {
    const gaMeasurementId = getGaMeasurementId();
    const yandexMetrikaId = getYandexMetrikaId();

    if (gaMeasurementId && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
      });
    }

    if (yandexMetrikaId && typeof window.ym === "function") {
      window.ym(Number(yandexMetrikaId), "hit", path, {
        title,
      });
    }
  });
}

export function trackArticleRead(article) {
  if (!article?.slug) {
    return;
  }

  trackEvent("article_read", {
    article_slug: article.slug,
    article_title: article.title,
    article_category: article.categorySlug || article.category,
    reading_time: article.readingTime || 0,
    lead_magnet: article.leadMagnet || "trial",
  });
}

export function trackLeadMagnetClick(payload = {}) {
  trackEvent("lead_magnet_click", payload);
}

export function trackLandingCtaClick(payload = {}) {
  trackEvent("landing_cta_click", payload);
}

export function trackTrialSignup(payload = {}) {
  trackEvent("trial_signup", payload);
}
